import React, {
    useState,
    useImperativeHandle,
    forwardRef,
    useRef,
    useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import {Dropdown} from 'semantic-ui-react';

interface DropDownFilterProps {
    dataSource: any[];
    field?: string;
    onNewFilterChange?: (field: string, value: string) => void;
    parentFilterInstance?: any;
    column: any; // Assuming column is passed down as a prop
    t:any;
}

const DropDownFilter = forwardRef((props: DropDownFilterProps, ref) => {
    const {dataSource, field, onNewFilterChange, parentFilterInstance, column,t} = props;
    const [filterText, setFilterText] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownOptions, setDropdownOptions] = useState<any[]>(dataSource);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const optionsRef = useRef<HTMLDivElement | null>(null); // Ref for the dropdown options
    const [dropdownPosition, setDropdownPosition] = useState<any>(null);

    // Polling logic to update the dataSource from the column's floating filter params
    useEffect(
        () => {
            const interval = setInterval(
                () => {
                    const latestData = column.getColDef().floatingFilterComponentParams ? column.getColDef().floatingFilterComponentParams.dataSource : [];
                    const formatted = latestData.map((item: any) => ({
                        key: item.key,
                        text: item.text,
                        value: item.value,
                    }));
                    setDropdownOptions((prev) => {
                        const prevStr = JSON.stringify(prev);
                        const nextStr = JSON.stringify(formatted);
                        if (prevStr !== nextStr) return formatted;
                        return prev;
                    });
                    if (latestData.length > 0) {
                        clearInterval(interval);
                    }
                },
                500
            ); // polling every 0.5s

            return () => clearInterval(interval);
        },
        [column]
    );

    // Open dropdown and calculate its position
    const handleDropdownToggle = () => {
        setIsOpen((prev) => !prev);
        if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    };

    // Notify parent filter change
    const onFilterChange = (value: string[]) => {
        setFilterText(value);
        if (onNewFilterChange && field) {
            onNewFilterChange(field, value.join(','));
        }
    };

    // Handle checkbox toggle
    const toggleOption = (value: string) => {
        const updated = filterText.includes(value)
            ? filterText.filter((v) => v !== value)
            : [...filterText, value];
        onFilterChange(updated);
    };

    // Close dropdown when clicked outside, but keep open if clicking on an option
    useEffect(
        () => {
            const handleClickOutside = (e: MouseEvent) => {
                if (
                    dropdownRef.current &&
                    !dropdownRef.current.contains(e.target as Node) &&
                    (!optionsRef.current || !optionsRef.current.contains(e.target as Node))
                ) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        },
        []
    );

    // Render the dropdown options with Portal for positioning outside AG Grid
    const renderDropdown = () => {
        if (!isOpen || !dropdownPosition) return null;

        return ReactDOM.createPortal(
            <div
                ref={optionsRef} // Add ref here to capture clicks inside the options
                style={{
                    position: 'absolute',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                    background: '#fff',
                    border: '1px solid #ccc',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    zIndex: 10000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                }}
            >
                {loading ? (
                    <div style={{padding: '10px', textAlign: 'center'}}>Loading...</div>
                ) : (
                    dropdownOptions.map((item) => (
                        <label
                            key={item.key}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 10px',
                                cursor: 'pointer',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={filterText.includes(item.value)}
                                onChange={() => toggleOption(item.value)}
                                style={{marginRight: '8px'}}
                            />
                            {item.text}
                        </label>
                    ))
                )}
            </div>,
            document.body
        );
    };

    return (
        <div ref={dropdownRef} style={{width: '100%', position: 'relative'}}>
            <div
                style={{
                    border: '1px solid #ccc',
                    padding: '10px 10px',
                    borderRadius: '4px',
                    background: '#fff',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
                onClick={handleDropdownToggle}
            >
                {filterText.length > 0
                    ? dropdownOptions
                        .filter((item) => filterText.includes(item.value))
                        .map((item) => item.text)
                        .join(', ')
                    : t("GLOBAL:PLEASE_SELECT")}
                <span style={{float: 'right'}}>▼</span>
            </div>
            {renderDropdown()}
        </div>
    );
});

export default DropDownFilter;



// WEBPACK FOOTER //
// ./src/components/AgGridFilter/DropdownFilter.tsx